import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Form, Row, Col, FormGroup, Label, Input, Card, CardBody, CardHeader, CardFooter, Button, Spinner, Alert } from "reactstrap";
import { Btn, H5 } from "../../../AbstractElements";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import IncomeService from "../../../Services/income/IncomeService";
import { toast } from "react-toastify";
import moment from 'moment';
import IncomePeriodDatePicker from './IncomePeriodDatePicker';

function IncomeStepOne({ handleNext, setIncomeId }) {
  const { control, handleSubmit, setValue, reset, watch, formState: { errors, isValid, isDirty } } = useForm({
    mode: 'onChange',
    defaultValues: {
      provider_id: '',
      currencies: '',
      amount: '',
      period_start: '',
      period_end: '',
      description: '',
      // false = private/testing (admin & staff only)
      visibility: false,
      file: null
    }
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [newFileSelected, setNewFileSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [fieldValidation, setFieldValidation] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [periodEndDate, setPeriodEndDate] = useState(null);
  const queryClient = useQueryClient();

  const { data: providers, isLoading: isProvidersLoading, error: providersError } = useQuery({
    queryKey: ["providers"],
    queryFn: IncomeService.getProviders,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: 1000,
  });

  // Watch form fields for real-time validation
  const watchedFields = watch();
  const selectedProviderId = watchedFields.provider_id;

  // Memoized provider options for performance
  const providerOptions = useMemo(() => {
    if (!providers) return [];
    return providers.map(provider => ({
      value: provider.id,
      label: provider.name,
      currency: provider.currency
    }));
  }, [providers]);

  // Memoized selected provider
  const selectedProvider = useMemo(() => {
    if (!selectedProviderId || !providers) return null;
    return providers.find(provider => provider.id === parseInt(selectedProviderId));
  }, [selectedProviderId, providers]);

  // Calculate form progress
  const calculateFormProgress = useCallback((fields) => {
    const requiredFields = ['provider_id', 'currencies', 'amount', 'period_start', 'period_end', 'file'];
    const filledFields = requiredFields.filter(field => {
      if (field === 'file') return newFileSelected;
      return fields[field] && fields[field].toString().trim() !== '';
    });
    return Math.round((filledFields.length / requiredFields.length) * 100);
  }, [newFileSelected]);

  // Update form progress when fields change
  useEffect(() => {
    const progress = calculateFormProgress(watchedFields);
    setFormProgress(progress);
  }, [watchedFields, calculateFormProgress]);

  // Effect to update the currency field when provider is selected
  useEffect(() => {
    if (selectedProvider) {
      setValue("currencies", selectedProvider.currency);
    }
  }, [selectedProvider, setValue]);

  // Enhanced validation rules
  const validationRules = useMemo(() => ({
    provider_id: {
      required: "Provider is required"
    },
    currencies: {
      required: "Currency is required"
    },
    amount: {
      required: "Amount is required",
      pattern: {
        value: /^\d+(\.\d{1,2})?$/,
        message: "Amount must be a valid number (e.g., 100.50)"
      },
      min: {
        value: 0.01,
        message: "Amount must be greater than 0"
      }
    },
    period_start: {
      required: "Period start is required",
      validate: (value) => {
        if (!value) return "Period start is required";
        const startDate = new Date(value);
        const today = new Date();
        if (startDate > today) return "Start date cannot be in the future";
        return true;
      }
    },
    period_end: {
      required: "Period end is required",
      validate: (value) => {
        if (!value) return "Period end is required";
        const endDate = new Date(value);
        const startDate = new Date(watchedFields.period_start);
        if (watchedFields.period_start && endDate <= startDate) {
          return "End date must be after start date";
        }
        return true;
      }
    },
    file: {
      required: "File is required",
      validate: () => newFileSelected || "File is required"
    },
    description: {
      maxLength: {
        value: 500,
        message: "Description must be less than 500 characters"
      }
    }
  }), [watchedFields.period_start, newFileSelected]);

  const addIncomeMutation = useMutation({
    mutationFn: IncomeService.addIncome,
    onSuccess: (data) => {
      toast.success("Income added successfully!");
      queryClient.invalidateQueries(["incomes"]);
      reset();
      setIsLoading(false);
      setNewFileSelected(false);
      setSelectedFile(null);
      setIncomeId(data.data.id); // Ensure the correct path to the ID
      handleNext();
    },
    onError: (error) => {
      toast.error(`Failed to add income: ${error.message}`);
      setIsLoading(false);
    },
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      let file = e.target.files[0];
      setSelectedFile(file);
      setNewFileSelected(true);
    } else {
      setSelectedFile(null);
      setNewFileSelected(false);
    }
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    const providerName = selectedProvider?.name || "Income";
    const periodLabel =
      data.period_start && data.period_end
        ? `${data.period_start} – ${data.period_end}`
        : data.period_start || "";
    const autoTitle = periodLabel ? `${providerName} ${periodLabel}` : providerName;

    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key !== "file" && key !== "title" && key !== "visibility") {
        formData.append(key, data[key]);
      }
    });
    formData.append("title", autoTitle);
    formData.append("visibility", data.visibility ? "1" : "0");
    if (newFileSelected) {
      formData.append("file", selectedFile);
    }
    addIncomeMutation.mutate(formData);
  };

  return (
    <>
      <style>{`
        .card-absolute .bg-primary h5,
        .card-absolute .bg-primary .h5 {
          color: #ffffff !important;
        }
      `}</style>
      <Col sm="12">
        <Card className="card-absolute" style={{ position: 'relative', overflow: 'visible', marginTop: '10px' }}>
          <CardHeader className="bg-primary" style={{ zIndex: 1 }}>
            <H5 style={{ color: '#ffffff'}}>Create Income</H5>
          </CardHeader>
          <CardBody style={{ paddingTop: '25px' }}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="provider">Provider</Label>
                    <Controller
                      name="provider_id"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Provider is required" }}
                      render={({ field }) => (
                        <Input {...field} type="select" id="provider" invalid={!!errors.provider_id}>
                          <option value="">Select Provider</option>
                          {providers && providers.map(provider => (
                            <option key={provider.id} value={provider.id}>
                              {provider.name}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                    {errors.provider_id && <div className="text-danger">{errors.provider_id.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="currencies">Currency</Label>
                    <Controller
                      name="currencies"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Currency is required" }}
                      render={({ field }) => <Input {...field} type="text" id="currencies" placeholder="Enter currency" invalid={!!errors.currencies} readOnly />}
                    />
                    {errors.currencies && <div className="text-danger">{errors.currencies.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="amount">Amount</Label>
                    <Controller
                      name="amount"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Amount is required" }}
                      render={({ field }) => <Input {...field} type="text" id="amount" placeholder="Enter amount" invalid={!!errors.amount} />}
                    />
                    {errors.amount && <div className="text-danger">{errors.amount.message}</div>}
                  </FormGroup>
                </Col>
               
                {/*  <Col md="4">
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Controller
                      name="status"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Status is required" }}
                      render={({ field }) => <Input {...field} type="text" id="status" placeholder="Enter status" invalid={!!errors.status} />}
                    />
                    {errors.status && <div className="text-danger">{errors.status.message}</div>}
                  </FormGroup>
                </Col> */}
              
                <Col md="6">
                  <FormGroup>
                    <Label for="period_start">Period Start</Label>
                    <Controller
                      name="period_start"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Period start is required" }}
                      render={({ field }) => (
                        <div className="date-picker">
                          <IncomePeriodDatePicker
                            selected={periodStartDate}
                            onChange={(date) => {
                              setPeriodStartDate(date);
                              field.onChange(date ? moment(date).format('YYYY-MM-DD') : '');

                              if (date) {
                                const monthEnd = moment(date).endOf('month');
                                setPeriodEndDate(monthEnd.toDate());
                                setValue('period_end', monthEnd.format('YYYY-MM-DD'), {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              } else {
                                setPeriodEndDate(null);
                                setValue('period_end', '', { shouldValidate: true });
                              }
                            }}
                            selectsStart
                            startDate={periodStartDate}
                            endDate={periodEndDate}
                            maxDate={new Date()}
                            placeholder="Select start date"
                            invalid={!!errors.period_start}
                          />
                        </div>
                      )}
                    />
                    {errors.period_start && <div className="text-danger">{errors.period_start.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="period_end">Period End</Label>
                    <Controller
                      name="period_end"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Period end is required" }}
                      render={({ field }) => (
                        <div className="date-picker">
                          <IncomePeriodDatePicker
                            selected={periodEndDate}
                            onChange={(date) => {
                              setPeriodEndDate(date);
                              field.onChange(date ? moment(date).format('YYYY-MM-DD') : '');
                            }}
                            selectsEnd
                            startDate={periodStartDate}
                            endDate={periodEndDate}
                            minDate={periodStartDate}
                            placeholder="Select end date"
                            invalid={!!errors.period_end}
                          />
                        </div>
                      )}
                    />
                    {errors.period_end && <div className="text-danger">{errors.period_end.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label for="visibility">Visibility</Label>
                    <Controller
                      name="visibility"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="select"
                          id="visibility"
                          value={field.value ? "1" : "0"}
                          onChange={(e) => field.onChange(e.target.value === "1")}
                          onBlur={field.onBlur}
                          innerRef={field.ref}
                        >
                          <option value="0">Private — Admins &amp; Staff only (testing)</option>
                          <option value="1">Shared — Labels &amp; Artists can access</option>
                        </Input>
                      )}
                    />
                    <small className="text-muted d-block mt-1">
                      Private incomes stay hidden from labels and artists, including related
                      royalties and distribution data. Switch to Shared when ready to publish.
                    </small>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label for="file">Invoice / PDF</Label>
                    <Controller
                      name="file"
                      control={control}
                      rules={{ required: "File is required" }}
                      render={({ field }) => (
                        <Input {...field} type="file" id="file" onChange={(e) => { handleFileChange(e); field.onChange(e); }} invalid={!!errors.file} />
                      )}
                    />
                    {errors.file && <div className="text-danger">{errors.file.message}</div>}
                  </FormGroup>
                </Col>

                <Col md="12">
                  <FormGroup>
                    <Label for="description">Description</Label>
                    <Controller
                      name="description"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="textarea"
                          id="description"
                          placeholder="Enter description"
                          invalid={!!errors.description}
                        />
                      )}
                    />
                    {errors.description && (
                      <div className="text-danger">{errors.description.message}</div>
                    )}
                  </FormGroup>
                </Col>

              </Row>
              <CardFooter className="text-end">
                <Btn
                  attrBtn={{
                    color: "primary",
                    className: "m-r-15",
                    type: "submit",
                    disabled: isLoading,
                  }}
                >
                  {isLoading ? "Submitting..." : "Next Step"}
                </Btn>
              </CardFooter>
            </Form>
          </CardBody>
        </Card>
      </Col>




    </>
  );
}

export default IncomeStepOne;